import { ElMessage } from 'element-plus';

import setFbq from '@/assets/js/fbq';
import ttq from '@/assets/js/tt-api';
import { Emarsys } from '@/common/emarsysSdk';
import { SDUtils } from '@/common/sensorsdata';

const PLATFORM_LANGUAGE_TEXT_MAP: Record<string, string> = {
  it: 'Italiano',
  pt: 'Português',
  es: 'Español',
  de: 'Deutsch',
  fr: 'Français',
  en: 'English',
};

type SensorsPlatformLanguageSDK = {
  registerPropertyPlugin?: (plugin: {
    properties: (data: { properties?: Record<string, unknown> }) => void;
  }) => void;
};

function getPlatformLanguageText(): string {
  try {
    const countryId = (localStorage.getItem('voo-country-id') || '').toLowerCase();
    return PLATFORM_LANGUAGE_TEXT_MAP[countryId] || 'English';
  } catch {
    return 'English';
  }
}

function setPlatformLanguageProperty(properties: Record<string, unknown>): void {
  const language = getPlatformLanguageText();
  const propertyKeys = Object.keys(properties);
  const platformNameIndex = propertyKeys.indexOf('pub_platform_name');

  // head.js 已经处理过时，避免客户端兜底插件再次重建 properties。
  if (
    properties.pub_platform_language === language &&
    ((platformNameIndex >= 0 && propertyKeys[platformNameIndex + 1] === 'pub_platform_language') ||
      (platformNameIndex === -1 &&
        propertyKeys[propertyKeys.length - 1] === 'pub_platform_language'))
  ) {
    return;
  }

  const nextProperties: Record<string, unknown> = {};
  let hasInserted = false;

  // 重建 properties 以控制日志展示顺序，让平台语言紧跟其它平台公共属性。
  Object.keys(properties).forEach((key) => {
    if (key === 'pub_platform_language') return;

    nextProperties[key] = properties[key];
    // 测试侧阅读日志时按插入顺序展示，这里固定放在 pub_platform_name 后面。
    if (key === 'pub_platform_name') {
      nextProperties.pub_platform_language = language;
      hasInserted = true;
    }
  });

  if (!hasInserted) {
    nextProperties.pub_platform_language = language;
  }

  Object.keys(properties).forEach((key) => {
    delete properties[key];
  });
  Object.assign(properties, nextProperties);
}

function registerSensorsPlatformLanguage(retryCount = 60): void {
  const sensors = (window.sensorsDataAnalytic201505 || window.sensors) as
    | SensorsPlatformLanguageSDK
    | undefined;

  if (!sensors?.registerPropertyPlugin) {
    if (retryCount > 0) {
      setTimeout(() => {
        registerSensorsPlatformLanguage(retryCount - 1);
      }, 100);
    }
    return;
  }

  // head.js 注册成功会设置该标记；未设置时才由客户端插件兜底注册。
  if (window.__vooglamSensorsPlatformLanguageRegistered) return;
  window.__vooglamSensorsPlatformLanguageRegistered = true;

  sensors.registerPropertyPlugin({
    properties(data: { properties?: Record<string, unknown> }) {
      if (data.properties) {
        setPlatformLanguageProperty(data.properties);
      }
    },
  });
}

export default defineNuxtPlugin((nuxtApp) => {
  nuxtApp.provide('fbq', setFbq);
  nuxtApp.provide('ttq', ttq);
  nuxtApp.provide('message', ElMessage);
  nuxtApp.vueApp.provide('fbq', setFbq);
  nuxtApp.vueApp.provide('ttq', ttq);
  if (process.client) {
    window.voo_sensors = SDUtils;
    SDUtils.syncSensorsIdentity();
    registerSensorsPlatformLanguage();
    (window as any).EmarsysTrack = Emarsys;

    /**
     * @description: 原生 flat 方法的 polyfill
     * @param {*} depth 深度，默认1
     * @return {*}
     */
    if (!Array.prototype.flat) {
      (Array.prototype as any).flat = function (depth = 1) {
        if (!this.length) return [];
        const reduceCb = (acc: any[], val: any) =>
          acc.concat(Array.isArray(val) ? val.flat(depth - 1) : val);
        return depth > 1
          ? this.reduce(reduceCb, [])
          : this.reduce((acc: any[], val: any) => acc.concat(Array.isArray(val) ? val : [val]), []);
      };
    }
  }
});
